import React, { FunctionComponent, useEffect, useState } from "react";
import { IAlbum } from "../../../hooks/albums";
import { Card, ListItemIcon, ListItemText, Menu, MenuItem, Stack, useTheme } from "@mui/material";
import { RightDetailListItem } from "../../RightDetailListItem";
import { strings } from "../../../localization/LocalizedStrings";
import { LabelsDesktop } from "../../attributes/labels/LabelsDesktop";
import { useUpdateAlbum } from "../../../hooks/albums/useUpdateAlbum";
import { AlertDialog } from "../../AlertDialog";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { toast } from "react-toastify";
import { useGetImage } from "../../../hooks/images";
import { ImageCache } from "../../../models/ImageCache";
import { LabelDialog } from "../../attributes/labels/LabelDialog";
import { useIsMobile } from "../../../hooks/isMobile";
import { useContextMenu } from "../../../hooks/contextMenu";
import { ILabel } from "../../../hooks/labels";
import { AttributeDialog } from "../../attributes/AttributeDialog";
import { Icon, IconType } from "../../../icons";
import { PiperDivider } from "../../PiperDivider";
import { AlbumIdentifiersTable } from "../identifiers/AlbumIdentifiersTable";
import { IAlbumIdentifier } from "../../../hooks/albumIdentifiers";
import { useDeleteAlbumIdentifier } from "../../../hooks/albumIdentifiers/useDeleteAlbumIdentifier";
import { useReorderAlbumIdentifiers } from "../../../hooks/albumIdentifiers/useReorderAlbumIdentifiers";
import { arrayMoveImmutable } from "array-move";
import { ModifyAlbumIdentifierDialog } from "../identifiers/ModifyAlbumIdentifierDialog";
import styles from "../AlbumFormatListSection/styles.module.scss";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { DialogState } from "../../../models/DialogState";

export const AlbumLabelListSection: FunctionComponent<IAlbumLabelListSectionProps> = ({
  isPublic,
  album,
  onAttributeUpdated
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [isLabelDialogOpen, setIsLabelDialogOpen] = useState(false);
  const [isLabelsDesktopOpen, setIsLabelsDesktopOpen] = useState(false);
  const [isLoadingLabel, setIsLoadingLabel] = useState(false);
  const { reorderAlbumIdentifiers } = useReorderAlbumIdentifiers();
  const { deleteAlbumIdentifier } = useDeleteAlbumIdentifier();
  const [
    isAlbumIdentifierDeleteConfirmationAlertOpen,
    setIsAlbumIdentifierDeleteConfirmationAlertOpen
  ] = useState(false);
  const [albumIdentifierDialogState, setAlbumIdentifierDialogState] = useState<
    DialogState<IAlbumIdentifier>
  >({ mode: "closed" });
  const [selectedAlbumIdentifier, setSelectedAlbumIdentifier] = useState<IAlbumIdentifier>();
  const [isLoadingAlbumIdentifiers, setIsLoadingAlbumIdentifiers] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const { updateAlbum } = useUpdateAlbum();
  const { getImage } = useGetImage();
  const [labelImage, setLabelImage] = useState<string>();
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const fetchLabelImage = async () => {
    const label = album.label;
    setLabelImage(label?.thumbnail);
    if (!label?.hasImage) return;
    const response = await getImage({
      name: label?.uuid,
      width: ImageCache.smallLabelImageSize,
      height: ImageCache.smallLabelImageSize
    });
    if (StatusCodesHelper.isSuccessful(response.status)) {
      if (label.uuid != album.label?.uuid) return;
      setLabelImage(response.body);
    }
  };
  const updateLabel = async (newValue: ILabel | undefined) => {
    setIsLabelsDesktopOpen(false);
    setIsLoadingLabel(true);
    const { status, body } = await updateAlbum({
      uuid: album.uuid,
      labelId: newValue?.uuid ?? null
    });
    if (!StatusCodesHelper.isSuccessful(status)) {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    } else {
      toast.success(strings.album_update_success);
      onAttributeUpdated(body);
    }
    setIsLoadingLabel(false);
  };
  useEffect(() => {
    fetchLabelImage().then();
  }, [album.label]);
  const createAlbumIdentifier = (newAlbumIdentifier: IAlbumIdentifier) => {
    if (album.albumIdentifiers) {
      album.albumIdentifiers.push(newAlbumIdentifier);
    } else {
      album.albumIdentifiers = [newAlbumIdentifier];
    }
    onAttributeUpdated(album);
  };
  const updateAlbumIdentifier = (newAlbumIdentifier: IAlbumIdentifier) => {
    album.albumIdentifiers = album.albumIdentifiers?.map(albumIdentifier =>
      albumIdentifier.uuid == newAlbumIdentifier.uuid ? newAlbumIdentifier : albumIdentifier
    );
    onAttributeUpdated(album);
  };
  const removeAlbumIdentifier = async () => {
    setIsLoadingAlbumIdentifiers(true);
    const { status, body } = await deleteAlbumIdentifier({ uuid: selectedAlbumIdentifier.uuid });
    if (!StatusCodesHelper.isSuccessful(status)) {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    } else {
      album.albumIdentifiers = album.albumIdentifiers?.filter(
        albumIdentifier => selectedAlbumIdentifier.uuid != albumIdentifier.uuid
      );
      onAttributeUpdated(album);
      toast.success(strings.generic_update_success);
    }
    setIsLoadingAlbumIdentifiers(false);
  };
  const moveAlbumIdentifiers = async (fromIndex: number, toIndex: number) => {
    const newlyOrderedAlbumIdentifiers = arrayMoveImmutable(
      album.albumIdentifiers,
      fromIndex,
      toIndex
    );
    setIsLoadingAlbumIdentifiers(true);
    const { status, body } = await reorderAlbumIdentifiers(newlyOrderedAlbumIdentifiers);
    if (StatusCodesHelper.isSuccessful(status)) {
      album.albumIdentifiers = newlyOrderedAlbumIdentifiers;
      onAttributeUpdated(album);
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoadingAlbumIdentifiers(false);
  };
  return (
    <div>
      {!(isPublic && album.label === undefined) && (
        <div>
          <RightDetailListItem
            disabled={isPublic}
            title={strings.label_title}
            detail={album.label?.name ?? strings.unknown}
            detailImage={labelImage}
            onClick={event => {
              if (album.label) {
                handleContextMenu(event);
              } else {
                setIsLabelsDesktopOpen(true);
              }
            }}
            isLoading={isLoadingLabel}
          />
          <PiperDivider />
        </div>
      )}
      <div className={styles.tableDiv}>
        <Stack className={styles.header} direction={"row"} spacing={2} sx={{ width: "100%" }}>
          <ListItemText
            disableTypography
            className={styles.title}
            secondary={strings.album_identifiers_title}
            sx={{ flexGrow: 0 }}
          />
          {!isPublic && (
            <ToolbarButton
              tooltip={strings.add_identifier}
              iconType={IconType.AddCircle}
              loading={isLoadingAlbumIdentifiers}
              onClick={() => setAlbumIdentifierDialogState({ mode: "add" })}
            />
          )}
        </Stack>
        {album.albumIdentifiers && album.albumIdentifiers.length > 0 && (
          <Card
            sx={{
              width: "100%",
              borderWidth: "1.5px",
              borderColor: theme.palette.background.paper,
              borderStyle: "solid"
            }}
          >
            <AlbumIdentifiersTable
              isPublic={isPublic}
              album={album}
              onModify={albumIdentifier =>
                setAlbumIdentifierDialogState({ mode: "edit", item: albumIdentifier })
              }
              onMoveUp={index => moveAlbumIdentifiers(index, index - 1)}
              onMoveDown={index => moveAlbumIdentifiers(index, index + 1)}
              onDelete={albumIdentifier => {
                setSelectedAlbumIdentifier(albumIdentifier);
                setIsAlbumIdentifierDeleteConfirmationAlertOpen(true);
              }}
            />
          </Card>
        )}
      </div>
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setIsLabelDialogOpen(true);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.FrameInspect} />
          </ListItemIcon>
          {strings.label_view}
        </MenuItem>
        {!isPublic && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setIsLabelsDesktopOpen(true);
            }}
            dense
          >
            <ListItemIcon>
              <Icon type={IconType.EditSquare} />
            </ListItemIcon>
            {strings.change_label}
          </MenuItem>
        )}
      </Menu>
      {album.label && (
        <LabelDialog
          label={album.label}
          isOpen={isLabelDialogOpen}
          onClose={() => setIsLabelDialogOpen(false)}
          canEdit={!isPublic}
        />
      )}
      <AttributeDialog open={isLabelsDesktopOpen} onClose={() => setIsLabelsDesktopOpen(false)}>
        <LabelsDesktop
          handleClose={() => {
            setIsLabelsDesktopOpen(false);
          }}
          isPublic={false}
          columnWidth={isMobile ? 12 : 6}
          preselectedId={album.label?.uuid}
          onLabelSelected={updateLabel}
          onClearValue={() => updateLabel(undefined)}
        />
      </AttributeDialog>
      <ModifyAlbumIdentifierDialog
        albumIdentifier={albumIdentifierDialogState.item}
        albumId={album.uuid}
        open={albumIdentifierDialogState.mode !== "closed"}
        onClose={() =>
          setAlbumIdentifierDialogState({ mode: "closed", item: albumIdentifierDialogState.item })
        }
        onUpdate={
          albumIdentifierDialogState.mode === "add" ? createAlbumIdentifier : updateAlbumIdentifier
        }
      />
      <AlertDialog
        message={strings.album_identifier_delete_confirmation}
        open={isAlbumIdentifierDeleteConfirmationAlertOpen}
        setOpen={setIsAlbumIdentifierDeleteConfirmationAlertOpen}
        isConfirm
        onConfirm={removeAlbumIdentifier}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
    </div>
  );
};

export interface IAlbumLabelListSectionProps {
  isPublic: boolean;
  album: IAlbum;
  onAttributeUpdated: (album: IAlbum) => void;
}
